import { GraphQLErrorCodes } from '../../constants/auth-routes';
import { SessionCsrfErrorCode, SessionErrorKind } from '../../constants/session-errors';

interface CsrfErrorBody {
  error?: string;
  code?: string;
}

function parseJsonBody(bodyText: string | undefined): unknown {
  if (!bodyText) {
    return undefined;
  }

  try {
    return JSON.parse(bodyText) as unknown;
  } catch {
    return undefined;
  }
}

export function isCsrfValidationFailureBody(body: unknown): body is CsrfErrorBody {
  if (typeof body !== 'object' || body === null) {
    return false;
  }

  const record = body as CsrfErrorBody;
  return record.error === SessionCsrfErrorCode.ValidationFailed || record.code === SessionCsrfErrorCode.BadToken;
}

export function classifyGraphQLErrorCode(code: string | undefined): SessionErrorKind {
  if (code === GraphQLErrorCodes.UNAUTHENTICATED) {
    return SessionErrorKind.Unauthenticated;
  }

  if (code === GraphQLErrorCodes.FORBIDDEN) {
    return SessionErrorKind.Forbidden;
  }

  return SessionErrorKind.Unknown;
}

interface GraphQLErrorExtensionsLike {
  code?: string;
  statusCode?: number;
}

/**
 * Classifies GraphQL error extensions. Nest UnauthorizedException is often
 * surfaced as code=HTTP_ERROR with statusCode=401 rather than UNAUTHENTICATED.
 * MFA_CHALLENGE_REQUIRED may also carry 401 and must not fall through to logout
 * or session recovery.
 */
export function classifyGraphQLErrorExtensions(extensions: GraphQLErrorExtensionsLike | undefined): SessionErrorKind {
  if (extensions?.code === GraphQLErrorCodes.MFA_CHALLENGE_REQUIRED) {
    return SessionErrorKind.Unknown;
  }

  const fromCode = classifyGraphQLErrorCode(extensions?.code);
  if (fromCode !== SessionErrorKind.Unknown) {
    return fromCode;
  }

  if (typeof extensions?.statusCode === 'number') {
    return classifyHttpStatus(extensions.statusCode);
  }

  return SessionErrorKind.Unknown;
}

export function classifyHttpStatus(statusCode: number, bodyText?: string): SessionErrorKind {
  if (statusCode === 401) {
    return SessionErrorKind.Unauthenticated;
  }

  if (statusCode === 403) {
    const body = parseJsonBody(bodyText);
    if (isCsrfValidationFailureBody(body)) {
      return SessionErrorKind.Csrf;
    }

    return SessionErrorKind.Unknown;
  }

  if (statusCode === 429 || statusCode >= 500) {
    return SessionErrorKind.Transient;
  }

  return SessionErrorKind.Unknown;
}

export function isTerminalAuthenticationFailure(kind: SessionErrorKind): boolean {
  return kind === SessionErrorKind.Unauthenticated;
}

export function shouldAttemptSessionRecovery(kind: SessionErrorKind): boolean {
  return kind === SessionErrorKind.Csrf || kind === SessionErrorKind.Unauthenticated;
}
